// Smart Daara - Sidebar Component (responsive)
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import { buildTenantPath } from '../../lib/routing';
import {
  LayoutDashboard, Users, GraduationCap, BookOpen, Settings,
  Building2, CreditCard, Bell, BarChart3, FolderOpen, UserCog,
  ChevronLeft, ChevronRight, BookMarked, Eye, Award, UserPlus, Send, X,
  CalendarDays, DoorOpen, ClipboardList, School
} from 'lucide-react';

export const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const { user, isSuperAdmin, isSchoolAdmin, isManager, isTeacher, isStudent, isParent } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const slug = user?.tenant_slug;
  // Helper: build a slug-prefixed path (falls back to unprefixed if slug missing)
  const p = (sub) => buildTenantPath(slug, sub);

  const getSidebarItems = () => {
    const isPhysical = user?.school_type === 'physical';
    const isCenseur = user?.role === 'censeur';
    const isAccountant = user?.role === 'accountant';
    if (isSuperAdmin) {
      return [
        { icon: LayoutDashboard, label: t('dashboard'), path: '/admin' },
        { icon: Building2, label: 'Écoles', path: '/admin/tenants' },
        { icon: CreditCard, label: t('subscription'), path: '/admin/plans' },
        { icon: Users, label: t('users'), path: '/admin/users' },
        { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
        { icon: Settings, label: t('settings'), path: '/admin/settings' },
      ];
    }
    // ----- PHYSICAL school menus (strict segmentation: no e-learning entries) -----
    if (isPhysical) {
      if (isSchoolAdmin || isManager || isCenseur || isAccountant) {
        const base = [{ icon: LayoutDashboard, label: t('dashboard'), path: p('school') }];
        if (isAccountant) {
          return [...base,
            { icon: CreditCard, label: t('subscription'), path: p('school/subscription') },
            { icon: Settings, label: t('settings'), path: p('school/settings') },
          ];
        }
        const mgmt = [
          { icon: School, label: language === 'fr' ? 'Année & paramètres' : 'Year & settings', path: p('school/physical/academic') },
          { icon: FolderOpen, label: language === 'fr' ? 'Niveaux & Classes' : 'Levels & Classes', path: p('school/physical/classes') },
          { icon: BookMarked, label: language === 'fr' ? 'Matières' : 'Subjects', path: p('school/physical/subjects') },
          { icon: DoorOpen, label: language === 'fr' ? 'Salles' : 'Rooms', path: p('school/physical/rooms') },
          { icon: UserCog, label: t('teachers'), path: p('school/physical/teachers') },
          { icon: GraduationCap, label: t('students'), path: p('school/physical/students') },
          { icon: ClipboardList, label: language === 'fr' ? 'Affectations' : 'Assignments', path: p('school/physical/assignments') },
          { icon: CalendarDays, label: language === 'fr' ? 'Emplois du temps' : 'Timetables', path: p('school/physical/timetable') },
          { icon: BookOpen, label: language === 'fr' ? 'Saisie des notes' : 'Grades entry', path: p('school/physical/grades') },
          { icon: Award, label: language === 'fr' ? 'Bulletins' : 'Report cards', path: p('school/physical/reports') },
        ];
        if (isCenseur) {
          return [...base, ...mgmt.filter((m) => !m.path.includes('/academic')),
            { icon: Settings, label: t('settings'), path: p('school/settings') }];
        }
        return [...base, ...mgmt,
          { icon: CreditCard, label: t('subscription'), path: p('school/subscription') },
          { icon: Settings, label: t('settings'), path: p('school/settings') },
        ];
      }
      if (isTeacher) {
        return [
          { icon: LayoutDashboard, label: t('dashboard'), path: p('teacher') },
          { icon: CalendarDays, label: language === 'fr' ? 'Mon emploi du temps' : 'My timetable', path: p('teacher/timetable') },
          { icon: BookOpen, label: language === 'fr' ? 'Saisie des notes' : 'Grades entry', path: p('teacher/grades') },
          { icon: Award, label: language === 'fr' ? 'Bulletins' : 'Report cards', path: p('teacher/reports') },
          { icon: Settings, label: t('settings'), path: p('teacher/settings') },
        ];
      }
      if (isStudent) {
        return [
          { icon: CalendarDays, label: language === 'fr' ? 'Mon emploi du temps' : 'My timetable', path: p('student') },
          { icon: Award, label: language === 'fr' ? 'Mes bulletins' : 'My report cards', path: p('student/report-cards') },
          { icon: Settings, label: t('settings'), path: p('student/settings') },
        ];
      }
      if (isParent) {
        return [
          { icon: CalendarDays, label: language === 'fr' ? 'Emplois du temps' : 'Timetables', path: p('parent') },
          { icon: Award, label: language === 'fr' ? 'Bulletins' : 'Report cards', path: p('parent/report-cards') },
          { icon: Settings, label: t('settings'), path: p('parent/settings') },
        ];
      }
      return [];
    }
    // ----- VIRTUAL school menus (unchanged) -----
    if (isSchoolAdmin || isManager) {
      return [
        { icon: LayoutDashboard, label: t('dashboard'), path: p('school') },
        { icon: FolderOpen, label: 'Classes', path: p('school/classes') },
        { icon: BookMarked, label: language === 'fr' ? 'Matières' : 'Subjects', path: p('school/subjects') },
        { icon: BookOpen, label: t('courses'), path: p('school/courses') },
        { icon: UserPlus, label: language === 'fr' ? 'Inscriptions' : 'Enrollments', path: p('school/enrollments') },
        { icon: GraduationCap, label: t('students'), path: p('school/students') },
        { icon: UserCog, label: t('teachers'), path: p('school/teachers') },
        { icon: Users, label: t('parents'), path: p('school/parents') },
        { icon: CreditCard, label: t('subscription'), path: p('school/subscription') },
        { icon: Send, label: language === 'fr' ? 'Reversements' : 'Payouts', path: p('school/payouts') },
        { icon: BarChart3, label: 'Analytics', path: p('school/analytics') },
        { icon: Settings, label: t('settings'), path: p('school/settings') },
      ];
    }
    if (isTeacher) {
      return [
        { icon: LayoutDashboard, label: t('dashboard'), path: p('teacher') },
        { icon: BookOpen, label: t('myCourses'), path: p('teacher/courses') },
        { icon: FolderOpen, label: t('subjects'), path: p('teacher/subjects') },
        { icon: UserPlus, label: language === 'fr' ? 'Inscriptions' : 'Enrollments', path: p('teacher/enrollments') },
        { icon: GraduationCap, label: t('students'), path: p('teacher/students') },
        { icon: BarChart3, label: t('progress'), path: p('teacher/progress') },
        { icon: Settings, label: t('settings'), path: p('teacher/settings') },
      ];
    }
    if (isStudent) {
      return [
        { icon: LayoutDashboard, label: t('dashboard'), path: p('student') },
        { icon: BookMarked, label: t('myCourses'), path: p('student/my-courses') },
        { icon: BookOpen, label: t('courseCatalog'), path: p('student/catalog') },
        { icon: ClipboardList, label: language === 'fr' ? 'Mes notes' : 'My grades', path: p('student/my-grades') },
        { icon: Award, label: language === 'fr' ? 'Certificats' : 'Certificates', path: p('student/certificates') },
        { icon: BarChart3, label: t('progress'), path: p('student/progress') },
        { icon: Settings, label: t('settings'), path: p('student/settings') },
      ];
    }
    if (isParent) {
      return [
        { icon: LayoutDashboard, label: t('dashboard'), path: p('parent') },
        { icon: Eye, label: 'Enfants', path: p('parent/children') },
        { icon: BarChart3, label: t('progress'), path: p('parent/progress') },
        { icon: Bell, label: t('notifications'), path: p('parent/notifications') },
        { icon: Settings, label: t('settings'), path: p('parent/settings') },
      ];
    }
    return [];
  };

  const items = getSidebarItems();

  const handleLinkClick = () => {
    // Auto-close on mobile
    if (typeof window !== 'undefined' && window.innerWidth < 768 && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-border/60 transition-all duration-300 z-40 overflow-y-auto pb-14",
          // Desktop width
          collapsed ? "md:w-16" : "md:w-64",
          // Mobile: full-height off-canvas (width fixed to 260px)
          "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Close button on mobile */}
        <button
          onClick={onMobileClose}
          className="absolute right-2 top-2 w-8 h-8 md:hidden flex items-center justify-center rounded-lg hover:bg-slate-100"
          data-testid="sidebar-mobile-close"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        {/* Desktop toggle */}
        <button
          onClick={onToggle}
          className="hidden md:flex absolute -right-3 top-6 w-6 h-6 bg-white border border-border rounded-full items-center justify-center shadow-sm hover:shadow-md transition-shadow"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          )}
        </button>

        {/* Navigation Items */}
        <nav className="p-3 space-y-1 pt-12 md:pt-3">
          {items.map((item) => {
            // The base dashboard path (e.g. /<slug>/school) is only "active" on exact match,
            // otherwise startsWith would highlight it on every sub-route.
            const isBase = /^\/(?:admin|[^/]+\/(?:school|teacher|student|parent))$/.test(item.path);
            const isActive = isBase
              ? location.pathname === item.path
              : location.pathname === item.path || location.pathname.startsWith(item.path + '/') || location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                  isActive
                    ? "bg-emerald-50 text-emerald-700 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
                data-testid={`sidebar-item-${item.path.split('/').pop()}`}
              >
                <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-emerald-700")} />
                {(!collapsed || mobileOpen) && (
                  <span className="truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Role Badge */}
        {(!collapsed || mobileOpen) && (
          <div className="absolute bottom-4 left-3 right-3">
            <div className="px-3 py-2 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 truncate">
                {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </p>
              <p className="text-sm font-medium text-slate-700 truncate">
                {user?.first_name} {user?.last_name}
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
